import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';
import { API_CONFIG } from '../config/api';

interface UserAccess {
  id: string;
  userId: string;
  materialId: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  material?: {
    id: string;
    title: string;
    description: string;
    referenceId?: string;
    referenceIds?: string[];
  };
}

interface UserAccessesState {
  accesses: UserAccess[];
  isLoading: boolean;
  error: string | null;
}

export const useUserAccesses = () => {
  const [userAccessesState, setUserAccessesState] = useState<UserAccessesState>({
    accesses: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  const fetchUserAccesses = useCallback(async (userId: string) => {
    setUserAccessesState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Usar o endpoint /accesses que retorna todos os acessos completos
      const response = await fetchWithAuth(API_ROUTES.ACCESSES.list(), {
        method: 'GET',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar acessos: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Filtrar acessos por userId
      const userAccesses = (data.accesses || []).filter((access: any) => access.userId === userId);
      
      // Buscar informações dos materiais para cada acesso
      const accessesWithMaterials = await Promise.all(
        userAccesses.map(async (access: any) => {
          try {
            // Buscar informações do material
            const materialResponse = await fetchWithAuth(`${API_CONFIG.BASE_URL}/materials/${access.materialId}`);
            if (materialResponse.ok) {
              const materialData = await materialResponse.json();
              return {
                ...access,
                material: {
                  id: materialData.id,
                  title: materialData.title,
                  description: materialData.description,
                  referenceId: materialData.referenceId,
                  referenceIds: materialData.referenceIds || []
                }
              };
            } else {
              return {
                ...access,
                material: {
                  id: access.materialId,
                  title: 'Material não encontrado',
                  description: '',
                  referenceId: 'N/A',
                  referenceIds: []
                }
              };
            }
          } catch (error) {
            return {
              ...access,
              material: {
                id: access.materialId,
                title: 'Erro ao carregar',
                description: '',
                referenceId: 'N/A',
                referenceIds: []
              }
            };
          }
        })
      );
      
      setUserAccessesState({
        accesses: accessesWithMaterials,
        isLoading: false,
        error: null,
      });

      return accessesWithMaterials;
    } catch (error) {
      let errorMessage = 'Erro desconhecido';
      
      if (error instanceof Error) {
        if (error.message.includes('Unauthorized')) {
          errorMessage = 'Sessão expirada. Faça login novamente.';
        } else if (error.message.includes('Unexpected token')) {
          errorMessage = 'Erro de comunicação com o servidor. Verifique se a API está funcionando.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setUserAccessesState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  const clearUserAccesses = useCallback(() => {
    setUserAccessesState({
      accesses: [],
      isLoading: false,
      error: null,
    });
  }, []);

  return {
    fetchUserAccesses,
    clearUserAccesses,
    userAccessesState,
  };
};
